import { z } from "zod";
import blueprintsJson from "../data/blueprints.json";
import commoditiesJson from "../data/commodities.json";
import engineerDetailsJson from "../data/engineer-details.json";
import engineersJson from "../data/engineers.json";
import experimentalEffectsJson from "../data/experimental-effects.json";
import materialsJson from "../data/materials.json";
import metaJson from "../data/META.json";
import modulesJson from "../data/modules.json";
import ranksJson from "../data/ranks.json";
import shipsJson from "../data/ships.json";
import {
  blueprintSchema,
  commodityDefSchema,
  engineerDefSchema,
  engineerDetailsFileSchema,
  experimentalEffectSchema,
  gameDataMetaSchema,
  materialSchema,
  moduleDefSchema,
  ranksSchema,
  shipSchema,
  type Blueprint,
  type CommodityDef,
  type EngineerDef,
  type EngineerDetails,
  type ExperimentalEffect,
  type GameDataMeta,
  type Material,
  type MaterialCategory,
  type ModuleDef,
  type Ranks,
  type Ship,
} from "./types.ts";

function parse<T>(dataset: string, schema: z.ZodType<T>, value: unknown): T {
  const result = schema.safeParse(value);
  if (!result.success) {
    throw new Error(
      `EDCC game data "${dataset}" failed validation. Re-run "npm run fetch:game-data" to regenerate it.\n${JSON.stringify(result.error.issues.slice(0, 10), null, 2)}`,
    );
  }
  return result.data;
}

const materialsFileSchema = z.object({
  generatedAt: z.string(),
  materials: z.object({
    raw: z.array(materialSchema),
    manufactured: z.array(materialSchema),
    encoded: z.array(materialSchema),
  }),
});

const blueprintsFileSchema = z.object({ generatedAt: z.string(), blueprints: z.array(blueprintSchema) });
const experimentalEffectsFileSchema = z.object({
  generatedAt: z.string(),
  effects: z.array(experimentalEffectSchema),
});
const shipsFileSchema = z.object({ generatedAt: z.string(), ships: z.array(shipSchema) });
const modulesFileSchema = z.object({ generatedAt: z.string(), modules: z.array(moduleDefSchema) });
const engineersFileSchema = z.object({ generatedAt: z.string(), engineers: z.array(engineerDefSchema) });
const commoditiesFileSchema = z.object({
  generatedAt: z.string(),
  commodities: z.array(commodityDefSchema),
});
const ranksFileSchema = z.object({ generatedAt: z.string(), ranks: ranksSchema });

const materialsFile = parse("materials.json", materialsFileSchema, materialsJson);
export const materials: Record<MaterialCategory, Material[]> = materialsFile.materials;
export const allMaterials: Material[] = [
  ...materialsFile.materials.raw,
  ...materialsFile.materials.manufactured,
  ...materialsFile.materials.encoded,
];
export const materialsByKey: Map<string, Material> = new Map(allMaterials.map((m) => [m.key, m]));

export const blueprints: Blueprint[] = parse(
  "blueprints.json",
  blueprintsFileSchema,
  blueprintsJson,
).blueprints;
export const blueprintsByKey: Map<string, Blueprint> = new Map(blueprints.map((b) => [b.key, b]));

/** Index by journal `BlueprintName` (fdname). May map to several module variants. */
export const blueprintsByFdName: Map<string, Blueprint[]> = (() => {
  const index = new Map<string, Blueprint[]>();
  for (const blueprint of blueprints) {
    if (!blueprint.fdName) continue;
    const list = index.get(blueprint.fdName) ?? [];
    list.push(blueprint);
    index.set(blueprint.fdName, list);
  }
  return index;
})();

export const experimentalEffects: ExperimentalEffect[] = parse(
  "experimental-effects.json",
  experimentalEffectsFileSchema,
  experimentalEffectsJson,
).effects;
export const experimentalEffectsByKey: Map<string, ExperimentalEffect> = new Map(
  experimentalEffects.map((e) => [e.key, e]),
);

export const ships: Ship[] = parse("ships.json", shipsFileSchema, shipsJson).ships;
export const shipsBySlug: Map<string, Ship> = new Map(ships.map((s) => [s.slug, s]));
export const shipsByFdSymbol: Map<string, Ship> = new Map(
  ships.filter((s) => s.fdSymbol).map((s) => [s.fdSymbol as string, s]),
);

export const modules: ModuleDef[] = parse("modules.json", modulesFileSchema, modulesJson).modules;
/**
 * Keyed by lowercased symbol: journal `Loadout`/`EngineerCraft` events use
 * lowercase module symbols (`int_engine_size5_class5`) while FDevIDs ships
 * them capitalized (`Int_Engine_Size5_Class5`).
 */
export const modulesBySymbol: Map<string, ModuleDef> = (() => {
  const index = new Map<string, ModuleDef>();
  for (const module of modules) {
    const key = module.symbol.toLowerCase();
    const current = index.get(key);
    if (!current || (current.category === "mercgear" && module.category !== "mercgear")) {
      index.set(key, module);
    }
  }
  return index;
})();

export const engineers: EngineerDef[] = (() => {
  const base = parse("engineers.json", engineersFileSchema, engineersJson).engineers;
  const detailsFile = parse(
    "engineer-details.json",
    engineerDetailsFileSchema,
    engineerDetailsJson,
  );
  const baseKeys = new Set(base.map((e) => e.key));
  const detailsByKey = new Map(detailsFile.engineers.map((entry) => [entry.key, entry]));

  const problems: string[] = [];
  if (detailsByKey.size !== detailsFile.engineers.length) {
    problems.push("engineer-details.json contains duplicate engineer keys");
  }
  for (const engineer of base) {
    if (!detailsByKey.has(engineer.key)) {
      problems.push(`engineer-details.json is missing an entry for "${engineer.key}"`);
    }
  }
  for (const entry of detailsFile.engineers) {
    if (!baseKeys.has(entry.key)) {
      problems.push(`engineer-details.json references unknown engineer "${entry.key}"`);
    }
    if (entry.kind === "on-foot" && entry.refersTo && !baseKeys.has(entry.refersTo)) {
      problems.push(`engineer "${entry.key}" refers to unknown engineer "${entry.refersTo}"`);
    }
  }
  if (problems.length > 0) {
    throw new Error(
      `EDCC game data "engineer-details.json" failed validation:\n- ${problems.join("\n- ")}`,
    );
  }

  return base.map((engineer): EngineerDef => {
    const entry = detailsByKey.get(engineer.key) as (typeof detailsFile.engineers)[number];
    const details: EngineerDetails =
      entry.kind === "ship"
        ? {
            kind: entry.kind,
            system: entry.system,
            base: entry.base,
            planet: entry.planet,
            region: entry.region,
            permitRequired: entry.permitRequired ?? false,
            discovery: entry.discovery,
            invite: entry.invite,
            unlock: entry.unlock,
            reputation: entry.reputation,
            referral: null,
            refersToKey: null,
            offers: [],
          }
        : {
            kind: entry.kind,
            system: entry.system,
            base: entry.base,
            planet: entry.planet,
            region: entry.region,
            permitRequired: entry.permitRequired ?? false,
            discovery: entry.discovery,
            invite: entry.invite,
            unlock: null,
            reputation: null,
            referral: entry.referral,
            refersToKey: entry.refersTo,
            offers: entry.offers,
          };
    return { ...engineer, ...details };
  });
})();
export const engineersByKey: Map<string, EngineerDef> = new Map(engineers.map((e) => [e.key, e]));
export const engineersByName: Map<string, EngineerDef> = new Map(engineers.map((e) => [e.name, e]));

export const commodities: CommodityDef[] = parse(
  "commodities.json",
  commoditiesFileSchema,
  commoditiesJson,
).commodities;
export const commoditiesByName: Map<string, CommodityDef> = new Map(
  commodities.map((c) => [c.name, c]),
);

export const ranks: Ranks = parse("ranks.json", ranksFileSchema, ranksJson).ranks;

export const meta: GameDataMeta = parse("META.json", gameDataMetaSchema, metaJson);
export const gameDataVersion: string = meta.generatedAt;
